import { Box, Flex, IconButton, ScrollArea, Text, TextArea } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { DeleteOutlineIcon, EditIcon, SaveIcon } from 'shared/icons';
import { DateUtilsService } from 'shared/utils/date-utils.service';
import { toast } from 'sonner';
import { CvCommentStore } from './CvComment.store';


type CommentData = {
   id: number | string
   comment: string | null;
   commentDate: string | null;
};

function CvCommentViewFn({
   store,
   commentData,
   refetch,
   maxWidth = 600
}: {
   store: CvCommentStore;
   commentData: CommentData
   refetch?: () => void;
   maxWidth?: number
}) {
   const { slug } = useParams();
   const [edit, setEdit] = useState<boolean>(false);

   const [comment, setComment] = useState(() => {
      return commentData.comment ?? '';
   });

   const textAreaRef = useRef<null | HTMLTextAreaElement>(null);

   const isAddLoading = store.addEmployerCommentState.status === 'pending';
   const isRemoveLoading = store.removeEmployerCommentState.status === 'pending';
   
   return (
      <Flex gap="3" width="100%"  maxWidth={maxWidth.toString() + 'px'}>
         <Box flexGrow="1">
            <Text color="gray" size="2">
               Коментар:
            </Text>

            {edit ? (
               <TextArea
                  placeholder="Додати коментар"
                  ref={textAreaRef}
                  size="2"
                  value={comment}
                  onChange={(e) => {
                     setComment(e.target.value);
                  }}
                  style={{ height: 80 }}
               />
            ) : (
               <ScrollArea
                  style={{
                     height: 80,
                     background: '#fffde5',
                     position: 'relative',
                     maxWidth: `${maxWidth.toString()}px`
                  }}
               >
                  <Flex direction="column">
                     <Box
                        pr="5"
                        pl="2"
                        py="1"
                        style={{ background: 'inherit', height: 80, borderRadius: '4px' }}
                     >
                        <Text size="2">{comment}</Text>
                     </Box>
                  </Flex>
               </ScrollArea>
            )}

            <Flex gap="2" mt="2" mr="1" justify="between">
               <Flex ml="1">
                  {commentData.commentDate && (
                     <Text color="gray" size="2">
                        {DateUtilsService.toAppFormat(commentData.commentDate)}
                     </Text>
                  )}
               </Flex>

               <Flex gap="2">
                  {!edit && (
                     <>
                        <IconButton
                           size="1"
                           variant="ghost"
                           onClick={() => {
                              setEdit(true);
                              setTimeout(() => {
                                 textAreaRef.current?.focus();
                              }, 0);
                           }}
                        >
                           <EditIcon width={20} height={20} />
                        </IconButton>

                        <IconButton
                           size="1"
                           variant="ghost"
                           loading={isRemoveLoading}
                           disabled={!comment}
                           onClick={() => {
                              store.removeEmployerComment(
                                 { id: commentData.id },
                                 {
                                    onSuccess() {
                                       if (slug) {
                                          refetch?.();
                                          setComment('');
                                          toast.warning('Коментар видалено');
                                       }
                                    },
                                    onError: () => {},
                                 }
                              );
                           }}
                        >
                           <DeleteOutlineIcon width={20} height={20} />
                        </IconButton>
                     </>
                  )}
                  {edit && (
                     <IconButton
                        size="1"
                        variant="ghost"
                        disabled={!comment}
                        loading={isAddLoading}
                        onClick={() => {
                           store.addEmployerComment(
                              { id: commentData.id, comment },
                              {
                                 onSuccess() {
                                    setEdit(false);
                                    if (slug) {
                                       refetch?.();
                                       setComment(comment);
                                       toast.success('Коментар збережено');
                                    }
                                 },
                                 onError: (e: string) => {
                                    toast.error(e);
                                 },
                              }
                           );
                        }}
                     >
                        <SaveIcon width={20} height={20} />
                     </IconButton>
                  )}
               </Flex>
            </Flex>
         </Box>
      </Flex>
   );
}

export const CvCommentView = observer(CvCommentViewFn);
